import React, { useRef, useState, useEffect, useLayoutEffect, useImperativeHandle } from 'react';
import _merge from 'lodash/merge';
import List, { ListProps } from 'components/List';
import SelectableButton from 'components/MoreButton/SelectableButton';
import Menu, { MenuProps } from 'components/Menu';
import { animated, useTransition } from 'react-spring';
import { TRANSITION_ACCELERATE } from '@nokia-csf-uxr/freeform-design-tokens/tokens/transition';
import { SPEED_FAST } from '@nokia-csf-uxr/freeform-design-tokens/tokens/speed';
import { getContext, useCombinedRefs, isAnimationDisabled } from 'common';
import KEY_CODES from 'common/KeyCodes';
import { CONSTANTS } from 'common/ListKeyboardNav';
import { getMenuMaxHeight } from 'common/MenuDimensionCalc';
import { getEasing, getDuration } from 'common/parse-tokens';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';

const { SPACING } = Tokens;

const {
  TAB,
  ESCAPE,
  ARROW_DOWN,
  ARROW_UP,
  ENTER_KEY,
  SPACE_KEY
} = KEY_CODES;

const { LIST_ITEM_TABABLE_ITEM } = CONSTANTS;

const DEFAULT_MENU_POSITIONING = {
  placement: 'bottom-end',
  modifiers: [
    {
      name: 'offset',
      options: {
        offset: [0, 4]
      }
    }
  ]
};

const MINIMUM_ANIMATION_PROPS = {
  leave: { opacity: 0 },
};

const ELEVATION_DARK = true;

export type MoreButtonHandle = {
  buttonRef: HTMLElement | React.RefObject<HTMLElement>,
  menuRef: HTMLElement,
  positionMenu: () => void
};
export interface MoreButtonProps extends React.ComponentPropsWithoutRef<'button'> {
  /** To render the menu items, consider using 'MoreButtonItem', 'MoreButtonItemDivider', 'MoreButtonItemGroupHeader'. */
  children: React.ReactNode;
  /** Control `menu` open state. */
  isOpen?: boolean;
  /** Override `Menu` props used for positioning actions menu with respect to icon button. */
  menuProps?: Partial<MenuProps>;
  /** Override `List` props that act as a container for the menu items inside menu. */
  listProps?: Partial<ListProps>;
  /** Override `IconButton` content. */
  buttonContent?: React.ReactNode;
  /** Any element ref that triggers the menu. Use when the trigger element is not the default IconButton */
  triggerEl?: React.RefObject<HTMLElement>;
  /**
   * Callback fired when the component requests to be opened.
   *
   * @param {object} event The event source of the callback.
   */
  onOpen?: (arg: React.MouseEvent | React.KeyboardEvent) => void;
  /**
   * Callback fired when the component requests to be closed.
   *
   * @param {object} event The event source of the callback.
   */
  onClose?: (arg: React.MouseEvent | React.KeyboardEvent) => void;
  /**
   * If it is empty object, animation on menu is disabled.
   * This uses React-spring transitions config. See https://react-spring.io for more information.
   */
  animationProps?: any;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** @ignore */
  onClick?: React.MouseEventHandler;
  /** @ignore */
  onKeyDown?: React.KeyboardEventHandler;
}

/** Summons an overflow menu in the style of a common menu.
 * Any other props than listed will be provided to the `<IconButton/>`, read the `<IconButton/>` documentation for more info.
 * To re-position menu on changes to children or target element, please use `positionMenu` callback using component ref.
 * The forwarded ref is available as 'buttonRef'.
*/
const MoreButton = React.forwardRef<MoreButtonHandle, MoreButtonProps>((props: MoreButtonProps, ref) => getContext(({ disableAnimation }) => {
  const {
    children,
    isOpen,
    menuProps,
    listProps,
    buttonContent,
    triggerEl,
    onOpen,
    onClose,
    onClick,
    onKeyDown,
    animationProps,
    disableAnimation: propsdisableAnimation,
    ...buttonProps
  } = props;
  const {
    positioningOptions,
    style: menuStyle,
    ...otherMenuProps
  } = menuProps;
  const {
    ref: passedListRef,
    style,
    elevationProps,
    onClick: onListClick,
    onKeyDown: onListKeyDown,
    ...otherListProps
  } = listProps;
  const applyTriggerFocus = useRef(false);
  const referenceElement = useRef(null);
  const listRef = useRef(null);
  const combinedListRef = useCombinedRefs(passedListRef, listRef);
  const [menuRef, setMenuRef] = useState(null);
  const [listMaxHeight, setListMaxHeight] = useState(null);
  const isMenuTriggeredByInternalBtn = !(triggerEl.current);

  const toggleMenu = (event) => {
    if (isOpen) {
      onClose && onClose(event);
    } else {
      onOpen && onOpen(event);
    }
  };

  const isMenuOnTop = () => {
    const trigger = isMenuTriggeredByInternalBtn ? referenceElement.current : triggerEl.current;
    const triggerElTopPos = trigger.getBoundingClientRect().top;
    const listTopPos = listRef.current.getBoundingClientRect().top;
    return listTopPos < triggerElTopPos;
  };

  const focusTriggerEl = () => {
    applyTriggerFocus.current = false;
    triggerEl.current && triggerEl.current.focus();
    referenceElement.current && referenceElement.current.focus();
  };

  const handleButtonClick = (event) => {
    toggleMenu(event);
    onClick && onClick(event);
  };

  const handleButtonKeyDown = (event) => {
    switch (event.key) {
      case ARROW_DOWN:
        event.preventDefault();
        if (isOpen) {
          !isMenuOnTop() && listRef.current && listRef.current.focus();
        } else {
          onOpen && onOpen(event);
        }
        break;
      case ARROW_UP:
        event.preventDefault();
        if (isOpen) {
          isMenuOnTop() && listRef.current && listRef.current.focus();
        }
        break;
      case ESCAPE:
        onClose && onClose(event);
        break;
      default:
        break;
    }
    onKeyDown && onKeyDown(event);
  };

  const handleListFocus = (event) => {
    if (event.target.getAttribute('role') === 'menu') {
      // get all list items
      const elements = listRef.current.querySelectorAll(`[class*=${LIST_ITEM_TABABLE_ITEM}]`);
      if (isMenuOnTop()) {
        // focus on last list item
        const last = elements[elements.length - 1];
        last && last.focus();
      } else {
        // focus on first list item
        const first = elements[0];
        first && first.focus();
      }
    }
  };

  const handleListClick = (event) => {
    event.preventDefault();
    applyTriggerFocus.current = true;
    onClose && onClose(event);
    onListClick && onListClick(event);
  };

  const handleListKeyDown = (event) => {
    switch (event.key) {
      case ESCAPE:
        applyTriggerFocus.current = true;
        onClose && onClose(event);
        break;
      case TAB:
        event.preventDefault();
        applyTriggerFocus.current = true;
        onClose && onClose(event);
        break;
      case ENTER_KEY:
      case SPACE_KEY:
        event.preventDefault();
        applyTriggerFocus.current = true;
        onClose && onClose(event);
        break;
      default:
        break;
    }
    onListKeyDown && onListKeyDown(event);
  };

  // Hook to make available the positionMenu function of the Menu component to the user.
  // User would call the moreButtonRef.positionMenu()
  useImperativeHandle(ref, () => ({
    buttonRef: isMenuTriggeredByInternalBtn ? referenceElement : triggerEl,
    menuRef,
    positionMenu: () => {
      menuRef && menuRef.positionMenu();
    }
  }), [menuRef]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    menuRef && menuRef.positionMenu();
  }, [isOpen, menuProps, listProps]);

  useEffect(() => {
    setListMaxHeight(getMenuMaxHeight(referenceElement.current));
  }, [referenceElement.current]);

  useEffect(() => {
    triggerEl.current && setListMaxHeight(getMenuMaxHeight(triggerEl.current));
  }, [triggerEl.current]);

  useEffect(() => {
    !isOpen && applyTriggerFocus.current && focusTriggerEl();
  }, [isOpen]);

  const transitions = useTransition(
    isOpen,
    null,
    animationProps ? _merge({}, MINIMUM_ANIMATION_PROPS, animationProps)
      : {
        from: { opacity: 0 },
        enter: {
          opacity: 1
        },
        leave: { opacity: 0 },
        config: {
          duration: getDuration(SPEED_FAST),
          easing: getEasing(TRANSITION_ACCELERATE)
        },
        immediate: isAnimationDisabled(propsdisableAnimation, disableAnimation)
      }
  );

  return (
    <>
      {isMenuTriggeredByInternalBtn && (
        <SelectableButton
          ref={referenceElement}
          aria-expanded={isOpen}
          selected={isOpen}
          {...buttonProps}
          onClick={handleButtonClick}
          onKeyDown={handleButtonKeyDown}
        >
          {buttonContent}
        </SelectableButton>
      )}
      {/* Ensure triggerEl is not undefined when Menu renders */}
      {(referenceElement.current || triggerEl.current) &&
      transitions.map(({ item, key, props: transitionStyles }) =>
        item && (
        <Menu
          key={key}
          ref={setMenuRef}
          isOpen={isOpen}
          triggerEl={isMenuTriggeredByInternalBtn ? referenceElement : triggerEl}
          usePortal
          as={animated.div}
          positioningOptions={{
            ...DEFAULT_MENU_POSITIONING,
            ...positioningOptions
          }}
          style={{ ...menuStyle, ...transitionStyles }}
          onClose={onClose}
          {...otherMenuProps}
        >
          <List
            role="menu"
            ref={combinedListRef}
            style={{
              maxHeight: listMaxHeight,
              overflowX: 'hidden' as const,
              padding: `${SPACING.SPACING_8} ${SPACING.SPACING_0}`,
              ...style
            }}
            onFocus={handleListFocus}
            onClick={handleListClick}
            onKeyDown={handleListKeyDown}
            elevationProps={{
              dark: ELEVATION_DARK,
              elevationIndex: 8,
              ...elevationProps
            }}
            {...otherListProps}
          >
            {children}
          </List>
        </Menu>
        ))}
    </>
  );
}));

MoreButton.displayName = 'MoreButton';

MoreButton.defaultProps = {
  isOpen: false,
  menuProps: {},
  listProps: {},
  buttonContent: undefined,
  triggerEl: {
    current: undefined
  },
  onOpen: undefined,
  onClose: undefined,
  animationProps: undefined,
  disableAnimation: undefined,
  onClick: undefined,
  onKeyDown: undefined
};

export default MoreButton;
